import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import Logo from '../media/logo.png';
import {
  isLocked,
  recordFailedAttempt,
  resetAttempts
} from '../components/util/loginThrottle';
import { startTokenExpiryTimer } from '../api/tokenExpiryHelper';

const API_URL = process.env.REACT_APP_API_URL;

const LoginPage = () => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState(false);
  const [isLoginLocked, setIsLoginLocked] = useState(false);

  const redirectUser = data => {
  const redirectUrl = localStorage.getItem('redirectAfterLogin');

  if (data.role === 'GLOBAL') {
    if (redirectUrl) {
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectUrl, { replace: true });
    } else {
      navigate('/global', { replace: true });
    }
    return;
  }

  if (data.role === 'MARKET') {
  //const pathSegments = window.location.pathname.split('/');  

  const apiMarket = data.market;
  const apiLang = Array.isArray(data.languageCode)
    ? data.languageCode[0]
    : data.languageCode;

  const apiLangShort = apiLang.split('-')[0];

  const finalMarket = apiMarket;
  const finalLang = apiLangShort;

  localStorage.setItem(
    'selectedMarketLang',
    JSON.stringify({
      countryCode: finalMarket,
      languageCode: finalLang
    })
  );

  localStorage.setItem('previousMarket', finalMarket);

  localStorage.removeItem('interactionData');
  localStorage.removeItem('salesArea');
  localStorage.removeItem('store');

  const redirectUrl = localStorage.getItem('redirectAfterLogin');

  if (redirectUrl) {
    const redirectSegments = redirectUrl.split('/').filter(Boolean);

    // Check market from stored URL
    const redirectMarket = redirectSegments[1];

    // Same market -> open original PPSI/PPI URL
    if (redirectMarket === finalMarket) {
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectUrl, { replace: true });
      return;
    }

    // Different market -> go to logged-in market menu
    localStorage.removeItem('redirectAfterLogin');
    navigate(`/dppsi/${finalMarket}/${finalLang}/menu`, {
      replace: true
    });
    return;
  }

  // Normal login flow
  navigate(`/dppsi/${finalMarket}/${finalLang}/menu`, {
    replace: true
  });
}

  navigate('/global', { replace: true });
};

  // Autofill saved credentials
  useEffect(() => {
    const savedUsername = localStorage.getItem('saved_username');

    if (savedUsername) setUsername(savedUsername);
  }, []);

  // Redirect immediately if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userDetails = localStorage.getItem('userDetails');

    if (token && userDetails) {
      try {
        const data = JSON.parse(userDetails);
        redirectUser(data);
      } catch (e) {
        console.error('Invalid userDetails', e);
      }
    }
  }, []);

  // Listen for login in another tab
  useEffect(() => {
    const handleStorageChange = event => {
      if (event.key === 'token' && event.newValue) {
        const userDetails = localStorage.getItem('userDetails');

        if (userDetails) {
          try {
            const data = JSON.parse(userDetails);
            redirectUser(data);
          } catch (e) {
            console.error('Failed to parse userDetails', e);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Login lock check
  useEffect(() => {
    const pathSegments = window.location.pathname.split('/');

    const currentMarket = pathSegments[2] || 'DEFAULT';

    const usernameToCheck = username;

    if (!usernameToCheck) return;

    const checkLockStatus = () => {
      if (isLocked(usernameToCheck, currentMarket)) {
        setIsLoginLocked(true);
        setError(
          'Too many failed attempts. Please try again in a little while.'
        );
      } else {
        setIsLoginLocked(false);

        setError(prev =>
          prev ===
          'Too many failed attempts. Please try again in a little while.'
            ? ''
            : prev
        );
      }
    };

    checkLockStatus();

    const interval = setInterval(checkLockStatus, 1000);

    return () => clearInterval(interval);
  }, [username]);

  const handleLogin = async () => {
    if (!username || !password) {
      setError('Please enter username and password');
      return;
    }

    const pathSegments = window.location.pathname.split('/');

    const currentMarket = pathSegments[2] || 'DEFAULT';
    const usernameToCheck = username;
    //|| getLockedUsername(currentMarket);
    if (usernameToCheck && isLocked(usernameToCheck, currentMarket)) {
      setIsLoginLocked(true);

      setError('Too many failed attempts. Please try again in a little while.');

      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        password
      });

      const data = response.data;

      resetAttempts(username, currentMarket);

      // Store full response
      localStorage.setItem('userDetails', JSON.stringify(data));
      localStorage.setItem('token', data.token);
      //localStorage.removeItem('redirectAfterLogin');
      // Store credentials after successful login
      localStorage.setItem('saved_username', username);
      // localStorage.setItem('tokenGeneratedAt', new Date().toISOString());

      // localStorage.setItem('tokenExpiresIn', data.expiresIn);
      localStorage.setItem(
        "tokenExpiry",
        JSON.stringify({
          generatedAt: new Date().toISOString(),
          expiresIn: data.expiresIn,
        })
      );

      // Remove all login attempt records
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('login_attempts_')) {
          localStorage.removeItem(key);
        }
      });

      // Remove saved redirect URL
      //localStorage.removeItem('redirectAfterLogin');

      startTokenExpiryTimer();

      console.log('Login Success:', data);

      redirectUser(data);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || '';
      if (errorMessage.toLowerCase().includes('username')) {
        setUsernameError(true);
        setError('Incorrect credentials');
      }
      recordFailedAttempt(username, currentMarket);

      if (isLocked(usernameToCheck, currentMarket)) {
        setIsLoginLocked(true);

        setError(
          'Too many failed attempts. Please try again in a little while.'
        );
      } else {
        if (!err.response?.data?.message.toLowerCase().includes('username')) {
          setError(err.response.data.message);
        } else {
          setError('Incorrect credentials');
        }
      }
    } finally {
      setLoading(false);
    }
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !loading && !isLoginLocked) {
      handleLogin();
    }
  };
  return (
    <div className="login-container">
      <div className="left-side"></div>
      <div className="right-side"></div>

      <div className="login-card">
        <div className="logo">
          <img src={Logo} alt="Logo" />
        </div>

        <h1>Welcome to iFoodPPSI</h1>

        <div className="form-group">
          <label>Username</label>
          <input
            type="text"
            className={usernameError ? 'input-error' : ''}
            placeholder="PPSIXX"
            value={username}
            onChange={e => {
              setUsername(e.target.value);
              setUsernameError(false);
              setError(''); // clear error message
            }}
            onKeyDown={handleKeyDown}
            disabled={isLoginLocked}
          />
        </div>

        <div className="form-group">
          <label>Password</label>
          <div className="password-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                setError(''); // clear error message
              }}
              onKeyDown={handleKeyDown}
              disabled={isLoginLocked}
            />
            <span
              className="toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              👁
            </span>
          </div>
        </div>

        {error && <p className="error-text">{error}</p>}

        <button
          className="login-btn"
          onClick={handleLogin}
          disabled={loading || isLoginLocked}
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;
